import { login as loginApi, getMe, getMyPermissions, changePassword as changePasswordApi } from '@/api/auth'
import { getToken, setToken, removeToken, getUserInfo, setUserInfo, removeUserInfo } from '@/utils/storage'

const MENU_PERMISSIONS_KEY = 'user_menu_permissions'

// camelCase → snake_case 转换：employeeProfile → employee_profile
function _toSnake(str) {
  if (!str || typeof str !== 'string') return str
  return str.replace(/([A-Z])/g, '_$1').toLowerCase()
}

/**
 * axios response interceptor 会把后端返回的业务数据（wrapper.data）
 * 递归做 snake_case → camelCase 转换。但 menuPermissions 的第一层 key
 * 是业务常量（permission_constants.py 中的 menu_key），必须保持 snake_case，
 * 否则前端 Sidebar / $hasPerm 用 snake 查不到。此函数把 menuPermissions 的
 * 第一层 key 强制转回 snake_case；action 层（read/create/edit 等）无下划线，不受影响。
 */
function _normalizeMenuPerms(perms) {
  if (!perms || typeof perms !== 'object') return {}
  const out = {}
  for (const k of Object.keys(perms)) {
    const snake = _toSnake(k)
    out[snake] = perms[k]
  }
  return out
}

// 初始化时从 localStorage 读取用户信息（包含 mustChangePassword 标记）
// 保证刷新页面后，强制改密码弹窗仍然弹出，不会因为 Vuex state 被重置而消失
const initUserInfo = getUserInfo()
const initVisible = !!(
  getToken() &&
  initUserInfo &&
  initUserInfo.mustChangePassword === true
)

// 初始化时从 localStorage 读取 menuPermissions（字符串→对象，失败兜底空对象）
let initMenuPermissions = {}
try {
  const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(MENU_PERMISSIONS_KEY) : null
  if (raw) initMenuPermissions = JSON.parse(raw) || {}
} catch (e) { initMenuPermissions = {} }

const state = {
  token: getToken() || '',
  userInfo: initUserInfo,
  menuPermissions: initMenuPermissions,
  // 首次登录强制改密对话框可见性（全局遮罩，不可关闭）
  forceChangePasswordVisible: initVisible
}

const mutations = {
  SET_TOKEN(state, token) {
    state.token = token
  },
  SET_USER_INFO(state, userInfo) {
    state.userInfo = userInfo
    // 任何地方刷新 userInfo（getInfo 返回、storage 重新拉取等）都自动同步弹窗可见性：
    // mustChangePassword=true 就弹，false 就关闭
    if (userInfo && userInfo.mustChangePassword === true) {
      state.forceChangePasswordVisible = true
    } else {
      state.forceChangePasswordVisible = false
    }
  },
  SET_MENU_PERMISSIONS(state, payload) {
    const perms = (payload && typeof payload === 'object') ? payload : {}
    state.menuPermissions = perms
    // 同步写 localStorage：Vuex 刷新页面后状态会清空，localStorage 持久化
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(MENU_PERMISSIONS_KEY, JSON.stringify(perms))
      }
    } catch (e) { /* ignore quota / private mode */ }
  },
  CLEAR_USER(state) {
    state.token = ''
    state.userInfo = null
    state.menuPermissions = {}
    state.forceChangePasswordVisible = false
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(MENU_PERMISSIONS_KEY)
      }
    } catch (e) { /* ignore */ }
  },
  SET_FORCE_CHANGE_PASSWORD(state, visible) {
    state.forceChangePasswordVisible = !!visible
  }
}

const actions = {
  async login({ commit }, { account, password, captchaId, captchaCode }) {
    const res = await loginApi(account, password, captchaId, captchaCode)
    const token = res.data.token
    commit('SET_TOKEN', token)
    setToken(token)
    return res
  },
  async getInfo({ commit }) {
    const res = await getMe()
    const userInfo = res.data
    commit('SET_USER_INFO', userInfo)
    setUserInfo(userInfo)
    return userInfo
  },
  /**
   * 拉取当前用户的权限（菜单+动作）配置并写入 Vuex + localStorage。
   * - admin: 返回空结构即可，前端 $hasPerm 对 admin 直接返回 true
   * - sub_admin: 返回真实的 { menuPermissions, isAdmin, isSubAdmin, menuSchema }
   * - employee: 返回空结构，前端统一走 EmployeeLayout（不进入管理端）
   * AdminLayout.created 每次进入管理端必调用，确保权限覆盖陈旧缓存
   */
  async loadPermissions({ commit }) {
    try {
      const res = await getMyPermissions()
      const data = (res && res.data) ? res.data : {}
      // 后端返回 snake_case menu_permissions，经 axios interceptor 转为 camelCase menuPermissions
      // 但 interceptor 递归把 menu_key（如 employee_profile）也误转成 camelCase，这里转回 snake
      const rawPerms = data.menuPermissions || data.menu_permissions || {}
      const perms = _normalizeMenuPerms(rawPerms)
      commit('SET_MENU_PERMISSIONS', perms)
      return data
    } catch (e) {
      // 拉取失败（如 token 过期）兜底清空，避免使用越权的陈旧缓存
      commit('SET_MENU_PERMISSIONS', {})
      throw e
    }
  },
  // 强制/首次改密码专用：成功后刷新本地 userInfo（mustChangePassword 会变 false），并关闭弹窗
  async forceChangePassword({ commit, dispatch }, { oldPassword, newPassword }) {
    await changePasswordApi({ oldPassword, newPassword })
    // 重新拉取用户信息：mustChangePassword 应该已被后端置为 False
    const userInfo = await dispatch('getInfo')
    if (userInfo && userInfo.mustChangePassword !== true) {
      commit('SET_FORCE_CHANGE_PASSWORD', false)
    }
    return userInfo
  },
  async logout({ commit }) {
    commit('CLEAR_USER')
    removeToken()
    removeUserInfo()
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
}
